import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { CSpinner } from '@coreui/react-pro'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import PanelQualityCard from './SideEncounterComponents/PanelQualityCard'
import TriangleAlert from './SideEncounterComponents/sharedPanelComponents/TriangleAlert'
import PanelDiagnosisCard from './SideEncounterComponents/PanelDiagnosisComponents/PanelDiagnosisCard'
import PanelOtherCard from './SideEncounterComponents/PanelOtherCard'
import PanelChargesCard from './SideEncounterComponents/PanelChargesComponents/PanelChargesCard'
import PanelSummaryCard from './SideEncounterComponents/PanelSummaryComponents/PanelSummaryCard'
import PanelVisitHistoryCard from './SideEncounterComponents/PanelVisitHistoryCard'
import PanelPatientCard from './SideEncounterComponents/PanelPatientComponents/PanelPatientCard'
import PanelFacilityCard from './SideEncounterComponents/PanelFacilityCard'
import FEATURES from '../../../utils/featuresConstants'
import { shouldShowNoteIcon } from '../../../utils/sharedFeaturesFunctions'
import PanelNoteCard from './SideEncounterComponents/PanelNoteComponents/PanelNoteCard'
import PanelPatientDocumentsCard from './SideEncounterComponents/PanelPatientComponents/PanelPatientDocumentsCard'
import PanelPatientInsurance from './SideEncounterComponents/PanelPatientComponents/PanelPatientInsurance'
import PanelPCCInfo from './SideEncounterComponents/PanelPatientComponents/PanelPCCInfo'
import {
  calculateVisitStatus,
  getCptEncTypeMismatch,
  VISIT_STATUSES,
} from './SideEncounterComponents/PanelSummaryComponents/panelSummaryHelpers'
import PanelHoldCard from './SideEncounterComponents/PanelHoldCard'
import PanelMipsListener from './SideEncounterComponents/sharedPanelComponents/PanelMipsListener'
import ProgressNoteIconAction from '../census/components/censusTable/ProgressNoteIconAction'
import { getIsNoteRequired } from '../../../utils/sharedChartFunctions'
import PanelPatientInsuranceCase from './SideEncounterComponents/PanelPatientComponents/PanelPatientInsuranceCase'

export default function EncounterPanel() {
  const panelEncounter = useSelector((state) => state.sidePanel.panelEncounter)
  const startSideIndex = useSelector((state) => state.sidePanel.startSideTabIndex)
  const panelChart = useSelector((state) => state.sidePanel.panelChart)
  const isLastEncounter = useSelector((state) => state.sidePanel.isLastEncounter)
  const aiCodeCheckFiring = useSelector((state) => state.sidePanel.aiCodeCheckFiring)

  const features = useSelector((state) => state.user.features) || []
  const practice = useSelector((state) => state.user.practice)
  const provider = useSelector((state) => state.user.provider)

  const [hideTabs, setHideTabs] = useState(false)

  // hack to let us change tabs without explicit control
  useEffect(() => {
    if (aiCodeCheckFiring) {
      setHideTabs(true)
      setTimeout(() => setHideTabs(false), 0)
    }
  }, [aiCodeCheckFiring])

  const isPCC = Boolean(panelEncounter?.pointclickcare?.orgUuid)
  const hasInsMapper = features?.includes(FEATURES.InsuranceMapper)

  return (
    <div id={'EncounterPanel'}>
      {!panelEncounter ? (
        <div className={'side-panel-spinner d-flex justify-content-center'}>
          <CSpinner />
        </div>
      ) : (
        <div id={'SidePanelEncounterDetails'}>
          {panelEncounter?.coding_ai && !isLastEncounter && (
            <div className="ai-full-size">
              <div className="ai-alert-text">This encounter contains ai generated data.</div>
              <div className="ai-subtext">Be sure to check for accuracy.</div>
            </div>
          )}

          <PanelSummaryCard />
          <PanelMipsListener />
          {!hideTabs && (
            <Tabs className={'side-tabs'} defaultIndex={startSideIndex} disableUpDownKeys>
              <TabList className={'side-tabs-list'}>
                <Tab>
                  <i className="fa-regular fa-square-dollar"></i>
                </Tab>
                <Tab>
                  <i className="fa-regular fa-address-book"></i>
                </Tab>
                <Tab>Hx</Tab>
                {isPCC && (
                  <Tab>
                    <i className="fa-regular fa-hospital-user"></i>
                  </Tab>
                )}
                {(shouldShowNoteIcon(features, { chart: panelChart }) ||
                  getIsNoteRequired(practice, provider)) && (
                  <Tab className={'react-tabs__tab note-icon-tab'}>
                    <div style={{ fontSize: '1.1rem', paddingRight: '10px' }}>
                      <ProgressNoteIconAction
                        item={{ ...panelEncounter, chart: panelChart }}
                        isHistory
                        disabled={isLastEncounter}
                      />
                    </div>
                  </Tab>
                )}
              </TabList>

              <TabPanel>
                <MainTabs />
              </TabPanel>

              <TabPanel>
                <Tabs className={'row-tabs'}>
                  <TabList className={'row-tabs-list'}>
                    <Tab>Demographics</Tab>
                    {isPCC && <Tab>Insurance</Tab>}
                    {hasInsMapper && <Tab>Ins Cases</Tab>}
                    <Tab>Documents</Tab>
                  </TabList>
                  <hr />

                  <TabPanel>
                    <PanelPatientCard />
                  </TabPanel>
                  {isPCC && (
                    <TabPanel>
                      <PanelPatientInsurance />
                    </TabPanel>
                  )}
                  {hasInsMapper && (
                    <TabPanel>
                      <PanelPatientInsuranceCase />
                    </TabPanel>
                  )}
                  <TabPanel>
                    <PanelPatientDocumentsCard />
                  </TabPanel>
                </Tabs>
              </TabPanel>

              <TabPanel>
                <PanelVisitHistoryCard />
              </TabPanel>
              {isPCC && (
                <TabPanel>
                  <PanelPCCInfo />
                </TabPanel>
              )}

              {(shouldShowNoteIcon(features, { chart: panelChart }) ||
                getIsNoteRequired(practice, provider)) && (
                <TabPanel className={'side-note-panel-tab'}>
                  <PanelNoteCard />
                </TabPanel>
              )}
            </Tabs>
          )}
        </div>
      )}
    </div>
  )
}

function MainTabs() {
  const features = useSelector((state) => state.user.features) || []
  const provider = useSelector((state) => state?.user?.provider)
  const practice = useSelector((state) => state?.user?.practice)
  const roles = useSelector((state) => state?.user?.roles) || []

  const startTopIndex = useSelector((state) => state.sidePanel.startTopTabIndex)
  const isLastEncounter = useSelector((state) => state.sidePanel.isLastEncounter)
  const status = useSelector((state) => state.sidePanel.panelEncounter?.status)
  const encounterType = useSelector((state) => state.sidePanel.panelEncounter?.encounterType)
  const selectedDx = useSelector((state) => state.sidePanel.panelEncounter?.diagnosisCodes) || []
  const cptCodes = useSelector((state) => state.sidePanel.panelEncounter?.procedureCodes)
  const panelMipsError = useSelector((state) => state.sidePanel.panelMipsError)
  const incident_to_provider_id = useSelector(
    (state) => state.sidePanel.panelEncounter?.incident_to_provider_id,
  )

  const [cptError, setCptError] = useState(false)

  const isHold =
    !isLastEncounter && calculateVisitStatus(isLastEncounter, status) === VISIT_STATUSES.Hold

  const isCPTExempt =
    practice?.medical_coding &&
    provider?.medical_coding !== false &&
    !roles.includes('Claimocity App Biller')

  useEffect(() => {
    setCptError(
      Boolean(
        getCptEncTypeMismatch({
          encounterType,
          emCode: cptCodes?.[0],
        }),
      ),
    )
  }, [encounterType, cptCodes])

  // ENSURE THIS MATCHES WITH SIDE_PANEL_TAB_ROW_INDEXES
  return (
    <Tabs className={'row-tabs'} defaultIndex={startTopIndex} disableUpDownKeys>
      <TabList className={'row-tabs-list'}>
        <Tab>
          Diagnosis
          {selectedDx?.length === 0 && <TriangleAlert />}
        </Tab>
        <Tab>
          Charges
          {!isCPTExempt && (cptCodes?.length === 0 || cptError) && <TriangleAlert />}
        </Tab>
        <Tab>Facility</Tab>
        <Tab>
          Other
          {incident_to_provider_id && <TriangleAlert />}
        </Tab>
        {features?.includes(FEATURES.VisitNotesMIPS) && (
          <Tab>
            Quality
            {panelMipsError && !isCPTExempt && <TriangleAlert />}
          </Tab>
        )}
        {isHold && <Tab>Hold</Tab>}
      </TabList>
      <hr />

      <TabPanel>
        <PanelDiagnosisCard />
      </TabPanel>
      <TabPanel forceRender>
        <PanelChargesCard />
      </TabPanel>
      <TabPanel>
        <PanelFacilityCard />
      </TabPanel>
      <TabPanel>
        <PanelOtherCard />
      </TabPanel>
      {features?.includes(FEATURES.VisitNotesMIPS) && (
        <TabPanel>
          <PanelQualityCard />
        </TabPanel>
      )}
      {isHold && (
        <TabPanel>
          <PanelHoldCard />
        </TabPanel>
      )}
    </Tabs>
  )
}
